import React, { useState, useEffect } from "react";
import { Layout, Table, Button, InputNumber, Input, Card, message, Typography, Space, Tag, Spin, Descriptions, Select, Empty } from "antd";
import { TruckOutlined, CloseCircleOutlined, CheckCircleOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import api from "../../../services/axiosInstance";
import dayjs from "dayjs";
import Headerdiv from "../../layout/header";
import Footerdiv from "../../layout/footer";
import "../../../styles/fluidDesign.css";
import AppBreadcrumb from "../../../components/common/AppBreadcrumb";
import { getStatusColor } from "../../../utils/statusHelper";

const { Content } = Layout;
const { Option } = Select;

const PrepareItems = () => {
    const navigate = useNavigate();
    const [requests, setRequests] = useState([]);
    const [selectedMrId, setSelectedMrId] = useState(undefined);
    const [selectedMr, setSelectedMr] = useState(null);
    const [issuingItems, setIssuingItems] = useState([]);
    const [loadingDropdown, setLoadingDropdown] = useState(false);
    const [loadingDetail, setLoadingDetail] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        fetchAcceptedRequests();
    }, []);

    const fetchAcceptedRequests = async () => {
        try {
            setLoadingDropdown(true);
            const response = await api.get("/masterdropdown/listof_accepted_material_requests.php");
            if (response.data.status === "success") {
                setRequests(response.data.dataset || []);
            } else {
                message.error("Failed to fetch accepted material requests.");
            }
        } catch (error) {
            message.error("Error connecting to material requests API.");
        } finally {
            setLoadingDropdown(false);
        }
    };

    const handleSelectMrById = async (mrId) => {
        setSelectedMrId(mrId);
        if (!mrId) {
            setSelectedMr(null);
            setIssuingItems([]);
            return;
        }

        const selectedMrSummary = requests.find(r => Number(r.mr_id) === Number(mrId));

        try {
            setLoadingDetail(true);
            // 1. Fetch MR details
            const response = await api.get(`/inventory/material_request/get.php?mr_id=${mrId}`);
            if (response.data.status !== "success" || !response.data.dataset) {
                message.error("Failed to load Material Request details.");
                return;
            }
            const mrDetail = response.data.dataset;

            // 2. Fetch existing issue notes for this MR to calculate already prepared quantities
            const issueNotesRes = await api.get("/inventory/issuing/list.php");
            let preparedQuantities = {}; // key: mri_id, val: sum of issuenote_qty
            if (issueNotesRes.data.status === "success") {
                const allNotes = issueNotesRes.data.dataset || [];
                const mrNotes = allNotes.filter(n => Number(n.mr_id) === Number(mrId) && n.iin_status !== 'Rejected');

                // For each note, we need to fetch its items and add them
                for (const note of mrNotes) {
                    const noteDetail = await api.get(`/inventory/issuing/list.php?iin_id=${note.iin_id}`);
                    if (noteDetail.data.status === "success" && noteDetail.data.dataset) {
                        const items = noteDetail.data.dataset.items || [];
                        items.forEach(itm => {
                            const mriId = Number(itm.mri_id);
                            const qty = Number(itm.issuenote_qty || 0);
                            preparedQuantities[mriId] = (preparedQuantities[mriId] || 0) + qty;
                        });
                    }
                }
            }

            // 3. Initialize items with remaining allowed quantities
            const items = (mrDetail.items || []).map(item => {
                const allocated = Number(item.issue_qty || 0); // allocated in MR accept
                const prepared = preparedQuantities[Number(item.mri_id)] || 0;
                const remaining = Math.max(0, allocated - prepared);
                const stock = Number(item.current_stock || 0);

                return {
                    ...item,
                    allocated_qty: allocated,
                    already_prepared: prepared,
                    remaining_to_issue: remaining,
                    new_issuenote_qty: remaining > 0 ? Math.min(remaining, stock) : 0,
                    serial_numbers: "",
                    special_details: ""
                };
            }).filter(item => item.allocated_qty > 0); // only show items that have been accepted/allocated in MR

            setSelectedMr(mrDetail);
            setIssuingItems(items);
        } catch (error) {
            message.error("Error loading issue preparation details.");
        } finally {
            setLoadingDetail(false);
        }
    };

    const handleQtyChange = (mri_id, val) => {
        setIssuingItems(prev => prev.map(item => {
            if (item.mri_id === mri_id) {
                return { ...item, new_issuenote_qty: val };
            }
            return item;
        }));
    };

    const handleTextChange = (mri_id, field, val) => {
        setIssuingItems(prev => prev.map(item => {
            if (item.mri_id === mri_id) {
                return { ...item, [field]: val };
            }
            return item;
        }));
    };

    const handleConfirmIssueNote = async () => {
        // Validation
        const invalidItems = issuingItems.filter(item => {
            const qty = Number(item.new_issuenote_qty || 0);
            return qty > item.remaining_to_issue || qty > Number(item.current_stock);
        });

        if (invalidItems.length > 0) {
            message.error("Some issue quantities exceed either remaining to issue quantity or warehouse stock.");
            return;
        }

        const totalToIssue = issuingItems.reduce((sum, item) => sum + Number(item.new_issuenote_qty || 0), 0);
        if (totalToIssue <= 0) {
            message.warning("Please enter an issue quantity greater than 0 for at least one item.");
            return;
        }

        try {
            setSubmitting(true);
            const payload = {
                mr_id: Number(selectedMr.mr_id),
                items: issuingItems.map(item => ({
                    mri_id: Number(item.mri_id),
                    ilvi_id: Number(item.ilvi_id),
                    issuenote_qty: Number(item.new_issuenote_qty || 0),
                    serial_numbers: item.serial_numbers || "",
                    special_details: item.special_details || ""
                }))
            };

            const response = await api.post("/inventory/issuing/create.php", payload);
            if (response.data.status === "success") {
                message.success(response.data.message || "Issue Note successfully created.");
                navigate("/material-issue-note/list");
            } else {
                message.error(response.data.message || "Failed to create issue note.");
            }
        } catch (error) {
            message.error("Error creating issue note.");
        } finally {
            setSubmitting(false);
        }
    };

    const itemColumns = [
        {
            title: "Item Code",
            dataIndex: "in_code",
            key: "in_code"
        },
        {
            title: "Item Name",
            dataIndex: "in_name",
            key: "in_name"
        },
        {
            title: "UOM",
            dataIndex: "uom_name",
            key: "uom_name",
            render: (text) => text || "-"
        },
        {
            title: "Accepted",
            dataIndex: "allocated_qty",
            key: "allocated_qty",
            align: 'right',
            render: (qty) => parseFloat(qty || 0).toFixed(2)
        },
        {
            title: "Prepared",
            dataIndex: "already_prepared",
            key: "already_prepared",
            align: 'right',
            render: (qty) => parseFloat(qty || 0).toFixed(2)
        },
        {
            title: "Remaining",
            dataIndex: "remaining_to_issue",
            key: "remaining_to_issue",
            align: 'right',
            render: (qty) => {
                const val = parseFloat(qty || 0);
                if (val <= 0) {
                    return <Tag color={getStatusColor('Issued')} style={{ fontWeight: 600 }}>Issued</Tag>;
                }
                return val.toFixed(2);
            }
        },
        {
            title: "Wh Stock",
            dataIndex: "current_stock",
            key: "current_stock",
            align: 'right',
            render: (qty) => {
                const stockVal = parseFloat(qty || 0);
                return <span style={{ color: stockVal === 0 ? 'red' : 'inherit', fontWeight: stockVal === 0 ? '600' : 'normal' }}>{stockVal.toFixed(2)}</span>;
            }
        },
        {
            title: "Issue Qty",
            key: "issue_qty",
            width: 130,
            render: (_, record) => {
                const maxAllowed = Math.min(record.remaining_to_issue, Number(record.current_stock));
                return (
                    <InputNumber
                        min={0}
                        max={maxAllowed}
                        step={0.01}
                        value={record.new_issuenote_qty}
                        onChange={(val) => handleQtyChange(record.mri_id, val)}
                        disabled={maxAllowed <= 0}
                        style={{ width: '100%' }}
                    />
                );
            }
        }
    ];

    const expandedRowRender = (record) => {
        const isDisabled = Number(record.new_issuenote_qty || 0) <= 0;
        return (
            <div style={{ 
                display: 'flex', 
                gap: '24px', 
                padding: '12px 8px',
                background: '#fafafa',
                borderRadius: '8px'
            }}>
                <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, color: '#595959', fontSize: 13 }}>
                        Serial Numbers
                    </label>
                    <Input.TextArea
                        placeholder="Enter serial numbers (one per line or comma separated)"
                        value={record.serial_numbers}
                        onChange={(e) => handleTextChange(record.mri_id, "serial_numbers", e.target.value)}
                        disabled={isDisabled}
                        rows={3}
                        style={{ borderRadius: '6px' }}
                    />
                </div>
                <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, color: '#595959', fontSize: 13 }}>
                        Special Details / Instructions
                    </label>
                    <Input.TextArea
                        placeholder="Enter any special instructions or notes"
                        value={record.special_details}
                        onChange={(e) => handleTextChange(record.mri_id, "special_details", e.target.value)}
                        disabled={isDisabled}
                        rows={3}
                        style={{ borderRadius: '6px' }}
                    />
                </div>
            </div>
        );
    };

    return (
        <Layout className="fluid-page">
            <Headerdiv />

            <div className="fluid-page-header">
                <div className="floating-shape shape-1"></div>
                <div className="floating-shape shape-2"></div>
                <AppBreadcrumb items={[
                    { label: 'Inventory', link: '/inventory-items' },
                    { label: 'Prepare Issue Note' },
                ]} />
                <h1 className="fluid-page-title">
                    <TruckOutlined style={{ marginRight: 12 }} />Prepare Issue Note
                </h1>
                
            </div>

            <Content className="fluid-content">
                <Card className="fluid-card" title="Select Accepted Material Request" style={{ marginBottom: 24 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                        <span style={{ fontWeight: 500, color: "#595959" }}>Material Request:</span>
                        <Select
                            showSearch
                            placeholder="Search and select accepted MR..."
                            value={selectedMrId}
                            onChange={handleSelectMrById}
                            style={{ width: 400 }}
                            allowClear
                            loading={loadingDropdown}
                            optionFilterProp="children"
                        >
                            {requests.map(mr => (
                                <Option key={mr.mr_id} value={mr.mr_id}>
                                    {mr.mr_ref} ({mr.creator_name})
                                </Option>
                            ))}
                        </Select>
                    </div>
                </Card>

                {loadingDetail && (
                    <div style={{ textAlign: 'center', padding: '40px 0' }}>
                        <Spin size="large" tip="Loading Material Request details..." />
                    </div>
                )}

                {!selectedMr && !loadingDetail && (
                    <Card className="fluid-card">
                        <Empty description="Please select an accepted Material Request from the dropdown above to start preparing items." />
                    </Card>
                )}

                {selectedMr && !loadingDetail && (
                    <>
                        <Card className="fluid-card" title="Request Overview" style={{ marginBottom: 24 }}>
                            <Descriptions bordered column={{ xxl: 4, xl: 3, lg: 3, md: 2, sm: 1, xs: 1 }} size="small">
                                <Descriptions.Item label="Reference Number"><strong>{selectedMr.mr_ref}</strong></Descriptions.Item>
                                <Descriptions.Item label="Status">
                                    <Tag color={getStatusColor(selectedMr.req_status)}>
                                        {selectedMr.req_status}
                                    </Tag>
                                </Descriptions.Item>
                                <Descriptions.Item label="Required Date">{dayjs(selectedMr.required_date).format("YYYY-MM-DD")}</Descriptions.Item>
                                <Descriptions.Item label="Requested By">{selectedMr.creator_name}</Descriptions.Item>
                                <Descriptions.Item label="Accepted By">{selectedMr.issuer_name || "-"}</Descriptions.Item>
                            </Descriptions>
                        </Card>

                        <Card className="fluid-card" title="Items to Issue">
                            <Table
                                dataSource={issuingItems}
                                columns={itemColumns}
                                rowKey="mri_id"
                                pagination={false}
                                bordered
                                size="middle"
                                scroll={{ x: 'max-content' }}
                                style={{ marginBottom: 24 }}
                                expandable={{
                                    expandedRowRender,
                                    defaultExpandAllRows: true,
                                    rowExpandable: (record) => record.remaining_to_issue > 0,
                                }}
                                rowClassName={(record) => record.remaining_to_issue <= 0 ? 'issued-row' : ''}
                            />

                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                                <Button
                                    icon={<CloseCircleOutlined />}
                                    onClick={() => handleSelectMrById(undefined)}
                                >
                                    Clear Selection
                                </Button>
                                <Button
                                    type="primary"
                                    icon={<CheckCircleOutlined />}
                                    onClick={handleConfirmIssueNote}
                                    loading={submitting}
                                    className="fluid-btn-primary"
                                >
                                    Prepare Items
                                </Button>
                            </div>
                        </Card>
                    </>
                )}
            </Content>

            <Footerdiv />
        </Layout>
    );
};

export default PrepareItems;
